import { useQuery } from "@tanstack/react-query";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { getCountryFlag } from "@/lib/countryFlags";
import { useState, useMemo, useEffect } from "react";
const SEASON_ID = "a07122d7-f1a7-4ce8-a92c-3c7266afebd6";

const STICKY_ROW_SHADOW = "shadow-[0_-2px_0_#0e0a0a,0_1px_0_hsl(var(--border))]";

const PODIUM_COLORS: Record<number, { text: string; border: string }> = {
  1: { text: "#FFD700", border: "rgba(255, 215, 0, 0.45)" },
  2: { text: "#C0C0C0", border: "rgba(192, 192, 192, 0.45)" },
  3: { text: "#CD7F32", border: "rgba(205, 127, 50, 0.45)" },
};

// ── Types ──
interface MatchupPick {
  user_id: string;
  matchup_id: string;
  selected_driver_number: number;
  is_correct: boolean | null;
}

interface RacePick {
  user_id: string;
  pole_driver_number: number | null;
  predicted_pole_margin: number | null;
  actual_pole_margin: number | null;
  pole_is_correct: boolean | null;
  sprint_p1_driver_number: number | null;
  sprint_p2_driver_number: number | null;
  sprint_p3_driver_number: number | null;
  sprint_p1_correct: boolean | null;
  sprint_p2_correct: boolean | null;
  sprint_p3_correct: boolean | null;
}

interface UserProfile {
  user_id: string;
  display_name: string | null;
  avatar_icon: string | null;
  team_colour: string | null;
}

interface ComparisonData {
  matchup_picks: MatchupPick[];
  race_picks: RacePick[];
  profiles: UserProfile[];
  positions: { user_id: string; position: number }[];
}

interface SeasonRound {
  race_id: string;
  round_number: number;
  meeting_name: string;
  country_name: string;
  is_sprint_weekend: boolean;
}

interface SeasonUserResult {
  user_id: string;
  display_name: string | null;
  avatar_icon: string | null;
  team_colour: string | null;
  rounds: { race_id: string; correct_picks: number }[];
}

interface SeasonSummaryData {
  rounds: SeasonRound[];
  user_results: SeasonUserResult[];
  positions: { user_id: string; position: number; pole_margin_delta: number }[];
}

export default function Results() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const raceFromUrl = searchParams.get("race");
  const leagueFromUrl = searchParams.get("league");
  const [selectedLeague, setSelectedLeague] = useState<string>(leagueFromUrl || "global");
  const [selectedRound, setSelectedRound] = useState<string>(raceFromUrl || "season");

  // Sync URL params on mount
  useEffect(() => {
    if (raceFromUrl && raceFromUrl !== selectedRound) {
      setSelectedRound(raceFromUrl);
    }
  }, [raceFromUrl]);

  useEffect(() => {
    if (leagueFromUrl && leagueFromUrl !== selectedLeague) {
      setSelectedLeague(leagueFromUrl);
    }
  }, [leagueFromUrl]);

  // ── Fetch user's leagues ──
  const { data: myLeagues = [] } = useQuery({
    queryKey: ["my-leagues-results", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("league_members")
        .select("league_id, leagues(id, name, emoji)")
        .eq("user_id", user!.id);
      if (error) throw error;
      return (data ?? []).map((m) => {
        const league = m.leagues as any;
        return { id: league.id, name: league.name, emoji: league.emoji || "🏆" };
      });
    },
  });

  // ── Fetch locked races for the round dropdown ──
  const { data: lockedRaces = [] } = useQuery({
    queryKey: ["locked-races-2026"],
    queryFn: async () => {
      const now = new Date().toISOString();
      // Get races where qualifying (or sprint qualifying) has started
      const { data: sessions, error: sErr } = await supabase
        .from("sessions")
        .select("race_id, session_type, date_start")
        .in("session_type", ["Qualifying", "Sprint Qualifying"])
        .lte("date_start", now);
      if (sErr) throw sErr;

      const lockedRaceIds = [...new Set(sessions?.map((s) => s.race_id) ?? [])];
      if (lockedRaceIds.length === 0) return [];

      const { data, error } = await supabase
        .from("races")
        .select("id, round_number, meeting_name, country_name, is_sprint_weekend, seasons!inner(year)")
        .eq("seasons.year", 2026)
        .in("id", lockedRaceIds)
        .order("round_number", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  // Auto-select latest locked race if no URL param and no manual selection
  useEffect(() => {
    if (!raceFromUrl && selectedRound === "season" && lockedRaces.length > 0) {
      // Keep "season" as default — user can switch
    }
  }, [lockedRaces]);

  const isSeasonView = selectedRound === "season";
  const leagueId = selectedLeague === "global" ? null : selectedLeague;

  // ── Drivers (for round detail view) ──
  const { data: drivers } = useQuery({
    queryKey: ["drivers", SEASON_ID],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("drivers")
        .select("*")
        .eq("season_id", SEASON_ID)
        .order("team_name")
        .order("driver_number");
      if (error) throw error;
      return data;
    },
  });

  const driverMap = useMemo(
    () => new Map(drivers?.map((d) => [d.driver_number, d]) ?? []),
    [drivers]
  );

  // ── Matchups (for round detail view) ──
  const { data: matchups } = useQuery({
    queryKey: ["matchups", selectedRound],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("matchups")
        .select("*")
        .eq("race_id", selectedRound)
        .in("status", ["published", "resolved"])
        .order("display_order");
      if (error) throw error;
      return data;
    },
    enabled: !isSeasonView && !!selectedRound,
  });

  // ── Round detail data ──
  const { data: comparison, isLoading: comparisonLoading } = useQuery({
    queryKey: ["compare-picks", selectedRound, leagueId],
    queryFn: async () => {
      const params: { p_race_id: string; p_league_id?: string } = {
        p_race_id: selectedRound,
      };
      if (leagueId) params.p_league_id = leagueId;
      const { data, error } = await supabase.rpc("get_race_picks_comparison", params);
      if (error) throw error;
      return data as unknown as ComparisonData;
    },
    enabled: !isSeasonView && !!selectedRound,
    staleTime: 60_000,
  });

  // ── Season summary data ──
  const { data: seasonSummary, isLoading: seasonLoading } = useQuery({
    queryKey: ["season-results-summary", leagueId],
    queryFn: async () => {
      const params: { p_league_id?: string } = {};
      if (leagueId) params.p_league_id = leagueId;
      const { data, error } = await supabase.rpc("get_season_results_summary", params);
      if (error) throw error;
      return data as unknown as SeasonSummaryData;
    },
    enabled: isSeasonView,
    staleTime: 60_000,
  });

  // ── Round detail transforms ──
  const { matchupPicksMap, racePicksMap, sortedUsers: roundUsers } = useMemo(() => {
    if (!comparison) return { matchupPicksMap: new Map(), racePicksMap: new Map(), sortedUsers: [] };

    const mpMap = new Map<string, MatchupPick>();
    for (const p of comparison.matchup_picks) {
      mpMap.set(`${p.user_id}-${p.matchup_id}`, p);
    }

    const rpMap = new Map<string, RacePick>();
    for (const rp of comparison.race_picks) {
      rpMap.set(rp.user_id, rp);
    }

    const posMap = new Map(
      (comparison.positions ?? []).map((p) => [p.user_id, p.position])
    );
    const users = [...comparison.profiles].sort((a, b) => {
      return (posMap.get(a.user_id) ?? 999) - (posMap.get(b.user_id) ?? 999);
    });
    const currentIdx = users.findIndex((u) => u.user_id === user?.id);
    if (currentIdx > 0) {
      const [me] = users.splice(currentIdx, 1);
      users.unshift(me);
    }

    return { matchupPicksMap: mpMap, racePicksMap: rpMap, sortedUsers: users };
  }, [comparison, user?.id]);

  // ── Season summary transforms ──
  const { seasonRounds, seasonUsers } = useMemo(() => {
    if (!seasonSummary) return { seasonRounds: [], seasonUsers: [] };

    const rounds = seasonSummary.rounds;

    // Build correct picks per user per race, merging matchup + race_picks rows
    const users = seasonSummary.user_results.map((u) => {
      const roundMap = new Map<string, number>();
      for (const r of u.rounds) {
        roundMap.set(r.race_id, (roundMap.get(r.race_id) ?? 0) + r.correct_picks);
      }
      const total = Array.from(roundMap.values()).reduce((sum, v) => sum + v, 0);
      return { ...u, roundMap, total };
    });

    // Sort by RPC position (canonical ranking with tiebreaker)
    const posMap = new Map(
      (seasonSummary.positions ?? []).map((p) => [p.user_id, p.position])
    );
    users.sort((a, b) => {
      return (posMap.get(a.user_id) ?? 999) - (posMap.get(b.user_id) ?? 999);
    });
    const currentIdx = users.findIndex((u) => u.user_id === user?.id);
    if (currentIdx > 0) {
      const [me] = users.splice(currentIdx, 1);
      users.unshift(me);
    }

    return { seasonRounds: rounds, seasonUsers: users };
  }, [seasonSummary, user?.id]);

  // ── Season positions & margin deltas (from RPC) ──
  const seasonPositions = useMemo(() => {
    if (!seasonSummary?.positions) return new Map<string, number>();
    return new Map(seasonSummary.positions.map((p) => [p.user_id, p.position]));
  }, [seasonSummary]);

  const seasonMarginDeltas = useMemo(() => {
    if (!seasonSummary?.positions) return new Map<string, number>();
    return new Map(seasonSummary.positions.map((p) => [p.user_id, p.pole_margin_delta]));
  }, [seasonSummary]);

  // ── User positions (from RPC) ──
  const userPositions = useMemo(() => {
    if (!comparison?.positions) return new Map<string, number>();
    return new Map(comparison.positions.map((p) => [p.user_id, p.position]));
  }, [comparison]);

  // ── Round detail columns ──
  const selectedRace = lockedRaces.find((r) => r.id === selectedRound);
  const isSprint = selectedRace?.is_sprint_weekend ?? false;
  const isResolved = matchups?.some((m) => m.status === "resolved") ?? false;

  type Column =
    | { type: "matchup"; id: string; headerA: string; headerB: string }
    | { type: "pole"; id: string }
    | { type: "margin"; id: string }
    | { type: "sprint"; id: string; label: string; posIndex: number };

  const columns: Column[] = useMemo(() => {
    if (isSeasonView || !matchups) return [];
    return [
      // Sprint picks first (if sprint weekend)
      ...(isSprint
        ? [
            { type: "sprint" as const, id: "sp1", label: "SP1", posIndex: 1 },
            { type: "sprint" as const, id: "sp2", label: "SP2", posIndex: 2 },
            { type: "sprint" as const, id: "sp3", label: "SP3", posIndex: 3 },
          ]
        : []),
      // Qualy: pole + margin
      { type: "pole" as const, id: "pole" },
      { type: "margin" as const, id: "margin" },
      // Race matchups
      ...matchups.map((m) => ({
        type: "matchup" as const,
        id: m.id,
        headerA: driverMap.get(m.driver_a_number)?.name_acronym ?? "???",
        headerB: driverMap.get(m.driver_b_number)?.name_acronym ?? "???",
      })),
    ];
  }, [isSeasonView, matchups, driverMap, isSprint]);

  function getCellData(userId: string, col: Column): { code: string; isCorrect: boolean | null; teamColour: string | null } {
    if (col.type === "matchup") {
      const pick = matchupPicksMap.get(`${userId}-${col.id}`);
      if (!pick) return { code: "—", isCorrect: null, teamColour: null };
      const driver = driverMap.get(pick.selected_driver_number);
      return { code: driver?.name_acronym ?? "???", isCorrect: pick.is_correct, teamColour: driver?.team_colour ?? null };
    }
    if (col.type === "pole") {
      const rp = racePicksMap.get(userId);
      if (!rp?.pole_driver_number) return { code: "—", isCorrect: null, teamColour: null };
      const driver = driverMap.get(rp.pole_driver_number);
      return { code: driver?.name_acronym ?? "???", isCorrect: rp.pole_is_correct, teamColour: driver?.team_colour ?? null };
    }
    if (col.type === "margin") {
      const rp = racePicksMap.get(userId);
      if (!rp) return { code: "—", isCorrect: null, teamColour: null };
      const predicted = rp.predicted_pole_margin ?? 0;
      if (rp.actual_pole_margin == null) {
        // Results not in yet — show prediction (0 if not set)
        const display = (predicted / 1000).toFixed(3);
        return { code: display, isCorrect: null, teamColour: null };
      }
      const delta = Math.abs(predicted - rp.actual_pole_margin) / 1000;
      return { code: delta.toFixed(3), isCorrect: null, teamColour: null };
    }
    if (col.type === "sprint") {
      const rp = racePicksMap.get(userId);
      if (!rp) return { code: "—", isCorrect: null, teamColour: null };
      const driverKey = `sprint_p${col.posIndex}_driver_number` as keyof RacePick;
      const correctKey = `sprint_p${col.posIndex}_correct` as keyof RacePick;
      const driverNum = rp[driverKey] as number | null;
      if (!driverNum) return { code: "—", isCorrect: null, teamColour: null };
      const driver = driverMap.get(driverNum);
      return { code: driver?.name_acronym ?? "???", isCorrect: rp[correctKey] as boolean | null, teamColour: driver?.team_colour ?? null };
    }
    return { code: "—", isCorrect: null, teamColour: null };
  }

  const isLoading = isSeasonView ? seasonLoading : comparisonLoading;

  return (
    <div className="px-4 py-6 md:px-8 md:py-10 max-w-5xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold md:text-3xl">Results</h1>

      {leagueFromUrl && (
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          ← Back to league
        </button>
      )}

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row">
        {/* League */}
        <Select value={selectedLeague} onValueChange={setSelectedLeague}>
          <SelectTrigger className="flex-1">
            <SelectValue placeholder="Global" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="global">Global</SelectItem>
            {myLeagues.map((l) => (
              <SelectItem key={l.id} value={l.id}>
                {l.emoji} {l.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Round */}
        <Select value={selectedRound} onValueChange={(v) => {
          setSelectedRound(v);
          // Update URL without adding to history
          if (v === "season") {
            searchParams.delete("race");
          } else {
            searchParams.set("race", v);
          }
          setSearchParams(searchParams, { replace: true });
        }}>
          <SelectTrigger className="flex-1">
            <SelectValue placeholder="Season 2026" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="season">Season 2026</SelectItem>
            {lockedRaces.map((race) => (
              <SelectItem key={race.id} value={race.id}>
                R{race.round_number} — {race.meeting_name} {getCountryFlag(race.country_name)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      ) : isSeasonView ? (
        <SeasonGrid
          rounds={seasonRounds}
          users={seasonUsers}
          currentUserId={user?.id ?? ""}
          userPositions={seasonPositions}
          marginDeltas={seasonMarginDeltas}
          onRoundClick={(raceId) => {
            setSelectedRound(raceId);
            searchParams.set("race", raceId);
            setSearchParams(searchParams, { replace: true });
          }}
        />
      ) : roundUsers.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">
          No picks submitted for this race yet.
        </p>
      ) : (
        <RoundGrid
          columns={columns}
          users={roundUsers}
          currentUserId={user?.id ?? ""}
          isResolved={isResolved}
          isSprint={isSprint}
          matchupCount={matchups?.length ?? 0}
          getCellData={getCellData}
          userPositions={userPositions}
        />
      )}
    </div>
  );
}

// ── Season Grid Component ──
function SeasonGrid({
  rounds,
  users,
  currentUserId,
  userPositions,
  marginDeltas,
  onRoundClick,
}: {
  rounds: SeasonRound[];
  users: { user_id: string; display_name: string | null; avatar_icon: string | null; team_colour: string | null; roundMap: Map<string, number>; total: number }[];
  currentUserId: string;
  userPositions: Map<string, number>;
  marginDeltas: Map<string, number>;
  onRoundClick: (raceId: string) => void;
}) {
  if (users.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">
        No results yet. Picks will appear here once a round is locked.
      </p>
    );
  }

  return (
    <div className="overflow-auto max-h-[calc(100vh-220px)] -mx-4 px-0 md:mx-0 md:px-0">
      <table className="min-w-max border-separate border-spacing-0">
        <thead>
          <tr>
            <th className="sticky left-0 top-0 z-30 bg-background px-3 py-2 text-left text-xs font-medium text-muted-foreground min-w-[140px] shadow-[0_2px_0_hsl(var(--background)),inset_0_-1px_0_hsl(var(--border))] border-r border-border">
              Player
            </th>
            {rounds.map((r) => (
              <th
                key={r.race_id}
                className="sticky top-0 z-20 bg-background px-2 py-2 text-center text-[10px] font-medium text-muted-foreground uppercase tracking-wide min-w-[44px] shadow-[0_2px_0_hsl(var(--background)),inset_0_-1px_0_hsl(var(--border))] cursor-pointer hover:text-foreground transition-colors"
                onClick={() => onRoundClick(r.race_id)}
                title={`${r.meeting_name} ${getCountryFlag(r.country_name)}`}
              >
                R{r.round_number}
              </th>
            ))}
            <th className="sticky top-0 z-20 bg-background px-2 py-2 text-center text-[10px] font-bold text-foreground uppercase tracking-wide min-w-[44px] shadow-[0_2px_0_hsl(var(--background)),inset_0_-1px_0_hsl(var(--border))] border-l border-border">
              Total
            </th>
            <th className="sticky top-0 z-20 bg-background px-2 py-2 text-center text-[10px] font-medium text-muted-foreground uppercase tracking-wide min-w-[52px] shadow-[0_2px_0_hsl(var(--background)),inset_0_-1px_0_hsl(var(--border))]">
              Delta
            </th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => {
            const isCurrentUser = u.user_id === currentUserId;
            const position = userPositions.get(u.user_id) ?? 0;
            const podium = position >= 1 && position <= 3 ? PODIUM_COLORS[position] : null;
            return (
              <tr
                key={u.user_id}
                className={cn(
                  "border-b border-border/50",
                  isCurrentUser && "sticky top-[33px] z-[11]"
                )}
              >
                <td
                  className={cn(
                    "sticky left-0 px-3 py-2 border-r border-border",
                    isCurrentUser ? `z-[12] top-[33px] ${STICKY_ROW_SHADOW}` : "z-10",
                    !isCurrentUser && "bg-background"
                  )}
                  style={{
                    ...(podium ? { borderLeftColor: podium.border, borderLeftWidth: 2 } : {}),
                    ...(isCurrentUser && u.team_colour ? { backgroundColor: `color-mix(in srgb, #${u.team_colour} 12%, #0a0a0a)` } : isCurrentUser ? { backgroundColor: "#0e0a0a" } : {}),
                  }}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="text-xs font-bold flex-shrink-0 w-7 text-center"
                      style={podium ? { color: podium.text } : { color: "hsl(var(--muted-foreground))" }}
                    >
                      P{position}
                    </span>
                    <span
                      className={cn("text-sm truncate max-w-[100px]", isCurrentUser && "font-semibold")}
                      style={isCurrentUser && u.team_colour ? { color: `#${u.team_colour}` } : undefined}
                    >
                      {u.display_name || "Unknown"}
                    </span>
                  </div>
                </td>
                {rounds.map((r) => {
                  const correct = u.roundMap.get(r.race_id) ?? 0;
                  return (
                    <td
                      key={r.race_id}
                      className={cn("px-2 py-2 text-center text-sm font-mono", isCurrentUser && `bg-[#0e0a0a] ${STICKY_ROW_SHADOW}`)}
                    >
                      {correct > 0 ? correct : <span className="text-muted-foreground/40">—</span>}
                    </td>
                  );
                })}
                <td className={cn("px-2 py-2 text-center text-sm font-mono font-bold border-l border-border", isCurrentUser && `bg-[#0e0a0a] ${STICKY_ROW_SHADOW}`)}>
                  {u.total}
                </td>
                <td className={cn("px-2 py-2 text-center text-sm font-mono text-muted-foreground", isCurrentUser && `bg-[#0e0a0a] ${STICKY_ROW_SHADOW}`)}>
                  {((marginDeltas.get(u.user_id) ?? 0) / 1000).toFixed(3)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Round Grid Component ──
function RoundGrid({
  columns,
  users,
  currentUserId,
  isResolved,
  isSprint,
  matchupCount,
  getCellData,
  userPositions,
}: {
  columns: { type: string; id: string; headerA?: string; headerB?: string; label?: string }[];
  users: UserProfile[];
  currentUserId: string;
  isResolved: boolean;
  isSprint: boolean;
  matchupCount: number;
  getCellData: (userId: string, col: Column) => { code: string; isCorrect: boolean | null; teamColour: string | null };
  userPositions: Map<string, number>;
}) {
  return (
    <div className="overflow-auto max-h-[calc(100vh-220px)] -mx-4 px-0 md:mx-0 md:px-0">
      <table className="min-w-max border-separate border-spacing-0">
        <thead>
          {/* Section label row */}
          <tr>
            <th className="sticky left-0 top-0 z-30 bg-background min-w-[140px] border-r border-border shadow-[0_2px_0_hsl(var(--background))]" />
            {isSprint && (
              <th
                colSpan={3}
                className="sticky top-0 z-20 bg-[#0a1f0a] px-2 py-1.5 text-center text-[10px] font-semibold text-green-400 uppercase tracking-wider shadow-[0_2px_0_#0a1f0a,inset_0_-1px_0_hsl(var(--border))]"
              >
                Sprint
              </th>
            )}
            <th
              colSpan={2}
              className="sticky top-0 z-20 bg-[#150a1f] px-2 py-1.5 text-center text-[10px] font-semibold text-purple-400 uppercase tracking-wider shadow-[0_2px_0_#150a1f,inset_0_-1px_0_hsl(var(--border))]"
            >
              Qualy
            </th>
            <th
              colSpan={matchupCount}
              className="sticky top-0 z-20 bg-[#1f0a0a] px-2 py-1.5 text-center text-[10px] font-semibold text-red-400 uppercase tracking-wider shadow-[0_2px_0_#1f0a0a,inset_0_-1px_0_hsl(var(--border))]"
            >
              Race
            </th>
            <th className="sticky top-0 z-20 bg-background shadow-[0_2px_0_hsl(var(--background)),inset_0_-1px_0_hsl(var(--border))]" />
          </tr>

          {/* Driver matchup headers */}
          <tr>
            <th className="sticky left-0 top-[29px] z-30 bg-background px-3 py-2 text-left text-xs font-medium text-muted-foreground min-w-[140px] border-r border-border shadow-[0_2px_0_hsl(var(--background)),inset_0_-1px_0_hsl(var(--border))]">
              Player
            </th>
            {columns.map((col) => (
              <th
                key={col.id}
                className="sticky top-[29px] z-20 bg-background px-2 py-2 text-center text-[10px] font-medium text-muted-foreground uppercase tracking-wide min-w-[52px] shadow-[0_2px_0_hsl(var(--background)),inset_0_-1px_0_hsl(var(--border))]"
              >
                {col.type === "matchup" ? (
                  <div className="leading-tight">
                    <div>{col.headerA}</div>
                    <div className="text-[8px] text-muted-foreground/60">v</div>
                    <div>{col.headerB}</div>
                  </div>
                ) : col.type === "pole" ? (
                  "Pole"
                ) : col.type === "margin" ? (
                  <span className="text-[9px]">Delta</span>
                ) : (
                  col.label
                )}
              </th>
            ))}
            <th className="sticky top-[29px] z-20 bg-background px-2 py-2 text-center text-[10px] font-medium text-muted-foreground uppercase tracking-wide min-w-[44px] shadow-[0_2px_0_hsl(var(--background)),inset_0_-1px_0_hsl(var(--border))]">
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {users.map((profile) => {
            const isCurrentUser = profile.user_id === currentUserId;
            const position = userPositions.get(profile.user_id) ?? 0;
            const podium = position >= 1 && position <= 3 ? PODIUM_COLORS[position] : null;
            return (
              <tr
                key={profile.user_id}
                className={cn(
                  "border-b border-border/50",
                  isCurrentUser && "sticky top-[79px] z-[11]"
                )}
              >
                <td
                  className={cn(
                    "sticky left-0 px-3 py-2 border-r border-border",
                    isCurrentUser ? `z-[12] top-[79px] ${STICKY_ROW_SHADOW}` : "z-10",
                    !isCurrentUser && "bg-background"
                  )}
                  style={{
                    ...(podium ? { borderLeftColor: podium.border, borderLeftWidth: 2 } : {}),
                    ...(isCurrentUser && profile.team_colour ? { backgroundColor: `color-mix(in srgb, #${profile.team_colour} 12%, #0a0a0a)` } : isCurrentUser ? { backgroundColor: "#0e0a0a" } : {}),
                  }}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="text-xs font-bold flex-shrink-0 w-7 text-center"
                      style={podium ? { color: podium.text } : { color: "hsl(var(--muted-foreground))" }}
                    >
                      P{position}
                    </span>
                    <span
                      className={cn("text-sm truncate max-w-[100px]", isCurrentUser && "font-semibold")}
                      style={isCurrentUser && profile.team_colour ? { color: `#${profile.team_colour}` } : undefined}
                    >
                      {profile.display_name || "Unknown"}
                    </span>
                  </div>
                </td>
                {(() => {
                  let correctCount = 0;
                  const cells = columns.map((col) => {
                    const { code, isCorrect, teamColour } = getCellData(profile.user_id, col);
                    const isEmpty = code === "—";
                    const showResult = isResolved && !isEmpty && isCorrect !== null;
                    if (col.type !== "margin" && isCorrect === true) correctCount++;

                    return (
                      <td
                        key={col.id}
                        className={cn(
                          "px-2 py-2 text-center text-sm font-mono border-border/30",
                          isCurrentUser ? `bg-[#0e0a0a] ${STICKY_ROW_SHADOW}` : "",
                          isEmpty && "text-muted-foreground/40",
                          showResult && isCorrect && (isCurrentUser ? "text-green-400" : "text-green-400 bg-green-500/10"),
                          showResult && !isCorrect && (isCurrentUser ? "text-red-400" : "text-red-400 bg-red-500/10")
                        )}
                        style={!showResult && !isEmpty && teamColour ? { color: `#${teamColour}` } : undefined}
                      >
                        {code}
                      </td>
                    );
                  });
                  return (
                    <>
                      {cells}
                      <td
                        className={cn(
                          "px-2 py-2 text-center text-sm font-bold",
                          isCurrentUser ? `bg-[#0e0a0a] ${STICKY_ROW_SHADOW}` : ""
                        )}
                      >
                        {correctCount}
                      </td>
                    </>
                  );
                })()}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
